import React, { useCallback, useEffect, useRef } from 'react';
import { Button, CloseButton, Toast } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

import { useLocalization } from '../../contexts/LocalizationContext';
import { useConfirm } from '../../hooks/useConfirm';
import { useEventListener } from '../../hooks/useEventListener';
import Loading from '../Loading';

export default function ConfirmToast() {
  const location = useLocation();
  const { localize } = useLocalization();
  const wrapperRef = useRef();
  const { close, setLoading, state } = useConfirm();

  useEffect(() => {
    close();
  }, [location.pathname]);

  useEventListener(
    'focusout',
    (e) => {
      // prevent focus leaving from ConfirmToast
      if (wrapperRef.current && wrapperRef.current.contains(e.target)) {
        wrapperRef.current.focus();
      }
    },
    document,
    []
  );

  useEventListener(
    'mousedown',
    (e) => {
      if (
        state.rootClose !== false &&
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target)
      ) {
        e.preventDefault();
        e.stopPropagation();
        close();
      }
    },
    document,
    [state.rootClose]
  );

  useEventListener(
    'keydown',
    (e) => {
      if (wrapperRef.current && e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
      }
    },
    document,
    []
  );

  const handleConfirm = useCallback(async () => {
    setLoading(true);
    state.onConfirm && (await state.onConfirm());
    setLoading(false);
    close();
  }, [state.onConfirm]);

  return (
    <div
      className='confirm-toast-backdrop-dark'
      style={{ display: state.show ? 'flex' : 'none' }}>
      <Toast
        show={state.show}
        onClose={close}
        ref={wrapperRef}
        className='border-0 confirm-toast'>
        <Toast.Header id='confirm-toast-header'>
          <strong className='mr-auto'>
            {state.header || localize('common.confirm')}
          </strong>
          {state.cancelButton && (
            <CloseButton className='close-btn' onClick={close} />
          )}
        </Toast.Header>

        <Toast.Body id='confirm-toast-body'>
          {state.loading && <Loading />}
          {state.body ? (
            state.body
          ) : (
            <>
              {state.message && (
                <div
                  style={{ whiteSpace: 'break-spaces', padding: '1rem 0rem' }}>
                  {state.message}
                </div>
              )}

              <div className='d-flex justify-content-end mx-2 mb-0 mt-2'>
                {state?.cancelButton && (
                  <Button
                    size='sm'
                    variant='secondary'
                    onClick={close}
                    disabled={state.loading}>
                    {state.cancelValue || localize('common.cancel')}
                  </Button>
                )}

                <Button
                  size='sm'
                  className='ms-1'
                  variant={state.variant || 'primary'}
                  disabled={state.loading}
                  onClick={handleConfirm}>
                  {state.okValue || 'OK'}
                </Button>
              </div>
            </>
          )}
        </Toast.Body>
      </Toast>
    </div>
  );
}
